import { useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { formatRelative } from '@/lib/dates'
import { formatMoney } from '@/lib/format'

type Range = '7d' | '30d' | '90d'

type Kpis = {
  total_cost_cad: number
  delta_pct: number | null
  tokens_in: number
  tokens_out: number
  tokens_total: number
  active_shops: number
  provisioned_shops: number
  avg_cost_per_active_shop_cad: number
  total_calls: number
}

type DayBucket = {
  date: string
  total_cad: number
  by_shop: Record<string, number>
}

type PerShop = {
  shop_id: string
  shop_name: string | null
  tokens_in: number
  tokens_out: number
  cost_cad: number
  calls: number
  last_called_at: string | null
}

type Resp = {
  range: Range
  days: number
  since: string
  kpis: Kpis
  daily_series: DayBucket[]
  per_shop: PerShop[]
}

export function AdminUsage() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const navigate = useNavigate()
  const [range, setRange] = useState<Range>('30d')

  const query = useQuery<Resp>({
    queryKey: ['admin', 'usage', range],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/admin/usage?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Resp
    },
  })

  async function exportCsv() {
    const token = await getToken()
    if (!token) return
    // Use Bearer-auth fetch + blob download so the API stays consistent
    // with the rest of /api/admin/* (no cookie-based path needed).
    const res = await fetch(`/api/admin/usage/csv?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `autoflo-usage-${range}-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const rangeLabelKey =
    range === '7d' ? 'range_7d' : range === '90d' ? 'range_90d' : 'range_30d'
  const rangeLabel = t(`usage.${rangeLabelKey}`)

  const maxDay = useMemo(() => {
    const series = query.data?.daily_series ?? []
    return Math.max(0, ...series.map((d) => d.total_cad))
  }, [query.data])

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {t('usage.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('usage.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangeButton
            current={range}
            value="7d"
            label={t('usage.range_7d')}
            onSelect={setRange}
          />
          <RangeButton
            current={range}
            value="30d"
            label={t('usage.range_30d')}
            onSelect={setRange}
          />
          <RangeButton
            current={range}
            value="90d"
            label={t('usage.range_90d')}
            onSelect={setRange}
          />
          <Button variant="outline" size="sm" onClick={exportCsv}>
            {t('usage.export_csv')}
          </Button>
        </div>
      </div>

      {query.isError ? (
        <Card className="border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : t('usage.error')}
        </Card>
      ) : null}

      {query.isLoading ? (
        <Card className="p-4 text-sm text-muted-foreground">
          {tCommon('loading')}
        </Card>
      ) : null}

      {query.data ? (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard
              label={t('usage.kpis.total_cost')}
              value={formatMoney(query.data.kpis.total_cost_cad)}
              meta={
                query.data.kpis.delta_pct === null
                  ? t('usage.kpis.delta_none')
                  : query.data.kpis.delta_pct >= 0
                    ? t('usage.kpis.delta_up', {
                        percent: Math.round(query.data.kpis.delta_pct),
                      })
                    : t('usage.kpis.delta_down', {
                        percent: Math.abs(
                          Math.round(query.data.kpis.delta_pct),
                        ),
                      })
              }
              metaTone={
                query.data.kpis.delta_pct === null
                  ? 'muted'
                  : query.data.kpis.delta_pct >= 0
                    ? 'up'
                    : 'down'
              }
              footnote={t('usage.kpis.total_cost_window', {
                range: rangeLabel.toLowerCase(),
              })}
            />
            <KpiCard
              label={t('usage.kpis.tokens')}
              value={formatLargeNumber(query.data.kpis.tokens_total)}
              meta={t('usage.kpis.tokens_breakdown', {
                in: formatLargeNumber(query.data.kpis.tokens_in),
                out: formatLargeNumber(query.data.kpis.tokens_out),
              })}
            />
            <KpiCard
              label={t('usage.kpis.active_shops')}
              value={String(query.data.kpis.active_shops)}
              meta={t('usage.kpis.active_shops_meta', {
                count: query.data.kpis.active_shops,
                provisioned: query.data.kpis.provisioned_shops,
              })}
            />
            <KpiCard
              label={t('usage.kpis.avg_cost')}
              value={formatMoney(
                query.data.kpis.avg_cost_per_active_shop_cad,
              )}
              meta={t('usage.kpis.avg_cost_meta', {
                perDay: formatMoney(
                  query.data.kpis.avg_cost_per_active_shop_cad /
                    Math.max(query.data.days, 1),
                ),
              })}
            />
          </div>

          {/* Chart */}
          <Card className="p-5">
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-foreground">
                  {t('usage.chart.header')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('usage.chart.subtitle', { range: rangeLabel.toLowerCase() })}
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t('usage.chart.today_partial')}
              </div>
            </div>
            <div className="flex h-40 items-end gap-1.5 px-1">
              {query.data.daily_series.map((d, i) => {
                const heightPct =
                  maxDay > 0 ? Math.max((d.total_cad / maxDay) * 100, 2) : 2
                const isToday = i === query.data!.daily_series.length - 1
                return (
                  <div
                    key={d.date}
                    title={`${d.date} · ${formatMoney(d.total_cad)}`}
                    style={{ height: `${heightPct}%` }}
                    className={
                      isToday
                        ? 'flex-1 rounded-sm bg-brand-light'
                        : 'flex-1 rounded-sm bg-brand'
                    }
                  />
                )
              })}
            </div>
          </Card>

          {/* Per-shop table */}
          {query.data.per_shop.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              {t('usage.empty')}
            </Card>
          ) : (
            <Card className="overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-3 font-semibold">
                        {t('usage.table.shop')}
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        {t('usage.table.tokens_in')}
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        {t('usage.table.tokens_out')}
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        {t('usage.table.cost')}
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        {t('usage.table.calls')}
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        {t('usage.table.avg_call')}
                      </th>
                      <th className="px-4 py-3 font-semibold">
                        {t('usage.table.last_call')}
                      </th>
                      <th className="w-8 px-2 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.per_shop.map((s) => (
                      <tr
                        key={s.shop_id}
                        className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-muted/30"
                        onClick={() =>
                          navigate(
                            `/usage/${encodeURIComponent(s.shop_id)}`,
                          )
                        }
                      >
                        <td className="px-4 py-3 font-semibold text-brand">
                          {s.shop_name ?? s.shop_id}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {s.tokens_in.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {s.tokens_out.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">
                          {formatMoney(s.cost_cad)}
                        </td>
                        <td className="px-4 py-3 text-right">{s.calls}</td>
                        <td className="px-4 py-3 text-right font-mono">
                          {s.calls > 0
                            ? formatMoney(s.cost_cad / s.calls)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {s.last_called_at
                            ? formatRelative(s.last_called_at)
                            : t('usage.table.never')}
                        </td>
                        <td className="px-2 py-3 text-right text-muted-foreground">
                          <ChevronRight className="ml-auto size-4" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      ) : null}
    </div>
  )
}

function RangeButton({
  current,
  value,
  label,
  onSelect,
}: {
  current: Range
  value: Range
  label: string
  onSelect: (v: Range) => void
}) {
  return (
    <Button
      variant={current === value ? 'default' : 'outline'}
      size="sm"
      onClick={() => onSelect(value)}
    >
      {label}
    </Button>
  )
}

function KpiCard({
  label,
  value,
  meta,
  metaTone,
  footnote,
}: {
  label: string
  value: string
  meta?: string
  metaTone?: 'up' | 'down' | 'muted'
  footnote?: string
}) {
  const metaClass =
    metaTone === 'up'
      ? 'text-emerald-700'
      : metaTone === 'down'
        ? 'text-rose-700'
        : 'text-muted-foreground'
  return (
    <Card className="p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-foreground">{value}</div>
      {meta ? <div className={`mt-1 text-xs ${metaClass}`}>{meta}</div> : null}
      {footnote ? (
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {footnote}
        </div>
      ) : null}
    </Card>
  )
}

function formatLargeNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return n.toString()
}
